/* eslint-disable max-len */
import { differenceInDays, differenceInYears } from 'date-fns';
import { darken } from 'polished';
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { useObserver } from '../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../Legacy';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { InfoView } from '../../ui/InfoView';
import { AppEvents } from '../AppEvents';
import { useResolve } from '../Container';
import { useDashboardVersion } from '../account/AccountHooks';
import { AccountModel } from '../account/AccountModel';
import { CopyToClipboard } from '../common/CopyToClipboard';
import { Em, Flexer } from '../dashboard/elements/LayoutElements';
import { inputSubmitPopup } from '../popups/InputPopup';
import { PopupManager } from '../popups/PopupManager';
import { Colors } from './../../ui';
import { ManageInformationModel, ManageInformationScope } from './ManageInformationScope';
import { ManageAccountImageView } from './new/ManageAccountImageView';

const { Section,
    LargeHeading, Heading, HeadingAction,
    DetailTable, DetailRow, DetailCell, DetailAction,
    ListItem, ListLabel, ListAction,
} = InfoView;

const CopyButton = styled.div`
    text-decoration: underline;
    cursor: pointer;
    user-select: none;

    &:active {
        color: ${darken(.1, Colors.text)};
    }
`;

const setNamePopup = (current: string, submit: (value: string) => Promise<void>) => inputSubmitPopup({
    title: 'Change Name',
    placeholder: 'e.g. Metro Retro Fan Club',
    current,
    copy: 'Please enter a new name for your team:',
    validate: x => (x || '').trim().length > 0 ? true : 'Please enter at least one character!',
    submit,
});

const ManageInformationLayout: LFC = () => {

    const api = useResolve(ApiClient);
    const events = useResolve(AppEvents);
    const popups = useResolve(PopupManager);
    const account = useObserver(useResolve(AccountModel));
    const info = useObserver(useResolve(ManageInformationModel));

    const ageYears = differenceInYears(new Date(), account.createdAt);
    const ageDays = differenceInDays(new Date(), account.createdAt) % 365;

    const onChangeName = useCallback(() => {
        popups.invoke(setNamePopup(account.label, async label => {
            const data = await api.exec(v2.admin.general.update({ label }));
            events.push('account:patch', { account: data });
        }));
    }, [account]);

    const version = useDashboardVersion();

    return <Flexer>
        <Section flush>
            <DetailTable>
                <DetailRow>
                    <DetailCell shrink>Organization Id:</DetailCell>
                    <DetailCell>
                        <CopyToClipboard value={account.id}>
                            <CopyButton>
                                {account.id}
                            </CopyButton>
                        </CopyToClipboard>
                    </DetailCell>
                </DetailRow>
                <DetailRow>
                    <DetailCell shrink>Name:</DetailCell>
                    <DetailCell>
                        <Em>{account.label}</Em>
                        <DetailAction onClick={onChangeName}>Change Name</DetailAction>
                    </DetailCell>
                </DetailRow>
                {version === 'next' && <DetailRow>
                    <DetailCell shrink>Icon:</DetailCell>
                    <DetailCell>
                        <Flexer flex="row">
                            <Flexer padding=".5 0 " shrink>
                                <ManageAccountImageView />
                            </Flexer>
                        </Flexer>
                    </DetailCell>
                </DetailRow>}
                <DetailRow>
                    <DetailCell shrink>Age:</DetailCell>
                    <DetailCell>
                        {!!ageYears && ` ${ageYears} year${ageYears !== 1 ? 's' : ''}`}
                        {!!ageDays && ` ${ageDays} day${ageDays !== 1 ? 's' : ''}`}
                        {!ageYears && !ageDays && '0 days'}
                    </DetailCell>
                </DetailRow>
                <DetailRow>
                    <DetailCell shrink>
                        People:
                    </DetailCell>
                    <DetailCell>
                        {info.users.owners} owners<br />
                        {info.users.admins} admins<br />
                        {info.users.members} members<br />
                        {info.users.guests} guests<br />


                    </DetailCell>

                </DetailRow>
                <DetailRow>
                    <DetailCell shrink>Teams:</DetailCell>
                    <DetailCell>
                        {info.workspaces}
                    </DetailCell>
                </DetailRow>
                <DetailRow>
                    <DetailCell shrink>Boards:</DetailCell>
                    <DetailCell>{info.boards}</DetailCell>
                </DetailRow>
                <DetailRow>
                    <DetailCell shrink>Objects:</DetailCell>
                    <DetailCell>{info.objects}</DetailCell>
                </DetailRow>
            </DetailTable>
        </Section>
    </Flexer>;
};

export const ManageInformationView: LFC = () => {
    return <ManageInformationScope>
        <ManageInformationLayout />
    </ManageInformationScope>;
};

